/**
 * Configuration provider component.
 *
 * Wraps the React application and loads configuration from the backend
 * via the `/api/config` endpoint. Handles loading states, errors,
 * and validation of configuration before rendering children.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { ConfigContext } from '@/context/ConfigContext'
import { api } from '@/services/api'
import type { Config, ConfigError } from '@/types/config'

interface ConfigProviderProps {
  children: ReactNode
  configUrl?: string
}

function ConfigErrorPage({ error }: { error: ConfigError }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-50">
      <div className="mx-auto max-w-md rounded-xl bg-white p-8 text-center shadow-lg">
        <div className="mb-4 text-4xl">⚠</div>
        <h1 className="mb-2 text-xl font-display font-bold text-red-600">
          Configuration Error
        </h1>
        <p className="mb-6 text-surface-600">
          {error.message ||
            'Failed to load application configuration. Please try again or contact support.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg bg-[#00ab81] px-6 py-2.5 font-medium text-white transition-colors hover:bg-[#00926f]"
        >
          Retry
        </button>
      </div>
    </div>
  )
}

function ConfigLoadingPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-50">
      <div className="text-center">
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-surface-200 border-t-[#00ab81]" />
        <p className="text-surface-500">Loading configuration...</p>
      </div>
    </div>
  )
}

function validateConfig(config: unknown): asserts config is Config {
  if (!config || typeof config !== 'object') {
    throw new Error('Configuration is not an object')
  }

  const cfg = config as Record<string, unknown>

  const requiredFields = ['api_base_url', 'app_name']
  for (const field of requiredFields) {
    if (typeof cfg[field] !== 'string' || !cfg[field]) {
      throw new Error(`Configuration missing required field: ${field}`)
    }
  }

  if (typeof cfg.debug !== 'boolean') {
    throw new Error('Configuration field "debug" must be a boolean')
  }

  if (typeof cfg.log_level !== 'string' || !cfg.log_level) {
    throw new Error('Configuration field "log_level" must be a non-empty string')
  }

  if (!cfg.features || typeof cfg.features !== 'object') {
    throw new Error('Configuration field "features" must be an object')
  }
}

export function ConfigProvider({ children, configUrl }: ConfigProviderProps) {
  const [config, setConfig] = useState<Config | null>(null)
  const [error, setError] = useState<ConfigError | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function loadConfig() {
      try {
        // Relativo al baseURL del cliente `api` (que ya es '/api'). Usar
        // '/api/config/' acá produciría '/api/api/config/' (doble prefijo).
        const url = configUrl || '/config/'

        const { data } = await api.get<Config>(url, {
          // Don't attach auth tokens for config endpoint
          headers: { Authorization: '' },
        })

        validateConfig(data)
        setConfig(data)
        setError(null)
        if (import.meta.env.DEV) {
          console.log('[ConfigProvider] Configuration loaded successfully')
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        const configError: ConfigError = {
          message: `Configuration could not be loaded: ${errorMessage}. Please contact support if this persists.`,
          code: 'CONFIG_LOAD_ERROR',
        }
        setError(configError)
        console.error('[ConfigProvider] Configuration loading failed:', configError)
      } finally {
        setIsLoading(false)
      }
    }

    loadConfig()
  }, [configUrl])

  if (error) return <ConfigErrorPage error={error} />
  if (isLoading || !config) return <ConfigLoadingPage />

  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
}
